import { ExportQueueStatus } from 'api/async-export';
import { TaskAPI } from 'api/tasks';
import type { Filter } from 'components/molecules/filters/types';
import type { ColumnOrder } from 'components/organisms/grid-custom-column';
import { FieldFilter } from 'components/organisms/tasks/filters';
import type { GroupFieldsMeta } from 'components/pages/filter-transformer';
import type { TaskField } from 'stores/fields';
import { getFieldString } from 'utils/export';
import type { FilterableField } from '../fields';
import type { GroupCriteria, GroupedFilter } from '../grouped-filter-popover';
import { isGroupFilter } from '../utils';
import { validateTaskColumns } from './utils';

interface Props {
  filter: GroupedFilter | Filter;
  projectId?: number;
  columnOrder: ColumnOrder[];
  lookup: GroupFieldsMeta | Record<string, TaskField>;
  failExport: () => void;
}

const getValidFilter = <T extends Props['filter']>(
  filter: T,
): GroupCriteria[] | FieldFilter[] => {
  if (isGroupFilter(filter)) {
    return filter.groupCriteria.filter(({ criteria }) =>
      FieldFilter.isValid(criteria),
    );
  }

  return filter.criteria.filter(FieldFilter.isValid);
};

const getValidColumnOrder = (
  columnOrder: ColumnOrder[],
  lookup: Record<string, TaskField>,
) => {
  return columnOrder
    .filter((x) => !x.hide)
    .map((x) => {
      const colId = x.colId === 'checkbox' ? 'taskName' : x.colId;

      if (colId) {
        const field = lookup[colId];

        if (field) {
          return getFieldString(field);
        }
      }

      return '';
    })
    .filter(Boolean);
};

export const getValidColumnOrderForGroups = (
  columnOrder: ColumnOrder[],
  lookup: GroupFieldsMeta,
) => {
  return columnOrder
    .filter((x) => !x.hide)
    .map((x) => {
      const colId = x.colId === 'checkbox' ? 'taskName' : x.colId;
      const splitColId = colId?.split('-');
      let field: FilterableField | undefined;

      if (splitColId) {
        if (splitColId?.length === 1) {
          // task field
          field = lookup[0].fields[splitColId[0]];
        } else {
          // project field
          field = lookup[1].fields[splitColId[1]];
        }
      }
      if (field) {
        return getFieldString(field);
      }

      return '';
    })
    .filter(Boolean);
};

export const exportAsCSV = ({
  filter,
  projectId,
  columnOrder,
  lookup,
  failExport,
}: Props): Promise<ExportQueueStatus> => {
  const validFilters = getValidFilter(filter);

  let validColumnOrder: string[];

  if (projectId) {
    validColumnOrder = getValidColumnOrder(
      columnOrder,
      lookup as Record<string, TaskField>,
    );
  } else {
    validColumnOrder = getValidColumnOrderForGroups(
      columnOrder,
      lookup as GroupFieldsMeta,
    );
  }
  if (validColumnOrder.length === 0) {
    failExport();

    return new Promise((_resolve, reject) => {
      reject(ExportQueueStatus.Failed);
    });
  }
  validColumnOrder = validateTaskColumns(validColumnOrder);

  const exportPromise = isGroupFilter(filter)
    ? TaskAPI.exportAsCSVAsyncForGroupType({
        filters: <GroupCriteria[]>validFilters,
        match: filter.type,
        columnOrder: validColumnOrder,
        projectId,
      })
    : TaskAPI.exportAsCSVAsyncForNonGroup({
        filters: <FieldFilter[]>validFilters,
        match: filter.type,
        columnOrder: validColumnOrder,
        projectId,
      });

  return exportPromise.then((res) => res.status);
};
